import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminClient, clearToken, getToken } from "@/lib/adminApi";
import { LogOut, Trash2, Mail, Phone, Calendar, Users, Star } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "bookings", label: "Reservas" },
  { key: "testimonials", label: "Reseñas" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    fetchAll();
  }, []); // eslint-disable-line

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        adminClient.get("/admin/bookings"),
        adminClient.get("/admin/testimonials"),
      ]);
      setBookings(b.data || []);
      setTestimonials(t.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    try {
      await adminClient.delete(`/admin/testimonials/${id}`);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
      toast.success("Reseña eliminada");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("¿Eliminar esta reserva?")) return;
    try {
      await adminClient.delete(`/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Reserva eliminada");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-[#1A1B1A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#F26419]">Sustrai</p>
            <h1 className="font-display font-black text-xl">Panel Admin</h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-[#F26419] hover:text-[#F26419] px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Salir
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 border-b-2 border-[#1A1B1A]/10 mb-8">
          {TABS.map((tt) => (
            <button
              key={tt.key}
              onClick={() => setTab(tt.key)}
              data-testid={`admin-tab-${tt.key}`}
              className={`px-5 py-3 text-sm tracking-[0.15em] uppercase font-bold transition-colors -mb-0.5 ${
                tab === tt.key
                  ? "text-[#1A1B1A] border-b-2 border-[#F26419]"
                  : "text-[#5C635A] hover:text-[#1A1B1A]"
              }`}
            >
              {tt.label} ({tt.key === "bookings" ? bookings.length : testimonials.length})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[#5C635A]">Cargando...</p>
        ) : tab === "bookings" ? (
          bookings.length === 0 ? (
            <p className="text-[#5C635A] italic">No hay reservas todavía.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {bookings.map((b) => (
                <article key={b.id} className="bg-white border-2 border-[#1A1B1A] p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display font-black text-xl text-[#1A1B1A]">{b.name}</h3>
                      <span className="inline-block mt-1 text-[10px] tracking-[0.2em] uppercase font-bold bg-[#F26419] text-white px-2 py-1">
                        {b.activity}
                      </span>
                    </div>
                    <button
                      onClick={() => deleteBooking(b.id)}
                      className="text-[#5C635A] hover:text-red-600 p-1"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5 text-sm text-[#1A1B1A]">
                    <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#2D5A3C]" /><a href={`mailto:${b.email}`} className="hover:text-[#F26419] break-all">{b.email}</a></p>
                    {b.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#2D5A3C]" /><a href={`tel:${b.phone}`}>{b.phone}</a></p>}
                    <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#2D5A3C]" />{b.date}</p>
                    <p className="flex items-center gap-2"><Users className="w-4 h-4 text-[#2D5A3C]" />{b.participants} pers. · nivel {b.experience_level || "—"}</p>
                  </div>
                  {b.message && (
                    <p className="mt-3 pt-3 border-t border-[#1A1B1A]/10 text-sm text-[#5C635A] whitespace-pre-wrap">{b.message}</p>
                  )}
                  <p className="mt-3 text-[10px] tracking-wider uppercase text-[#5C635A]">Recibida: {fmtDate(b.created_at)}</p>
                </article>
              ))}
            </div>
          )
        ) : testimonials.length === 0 ? (
          <p className="text-[#5C635A] italic">No hay reseñas todavía.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <article key={t.id} className="bg-white border-2 border-[#1A1B1A] p-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < (t.rating || 5) ? "fill-[#F26419] text-[#F26419]" : "text-[#D1D5DB]"}`} />
                    ))}
                  </div>
                  <button onClick={() => deleteTestimonial(t.id)} className="text-[#5C635A] hover:text-red-600 p-1" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-display text-lg leading-snug text-[#1A1B1A]">&ldquo;{t.message}&rdquo;</p>
                <p className="mt-4 pt-3 border-t border-[#1A1B1A]/10 text-sm font-bold text-[#2D5A3C]">
                  {t.name}{t.location ? ` · ${t.location}` : ""}
                </p>
                <p className="text-[10px] tracking-wider uppercase text-[#5C635A] mt-1">
                  {t.activity || "—"} · {fmtDate(t.created_at)}
                </p>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
